import React, { useState, useEffect, useRef } from 'react';
import { Star, ChevronLeft, ChevronRight, CheckCircle, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';
import Slider from 'react-slick';
import axios from 'axios';
import API_BASE_URL from '../config/api';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const GoogleReviewsPremium = () => {
  const [reviewsData, setReviewsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedReviews, setExpandedReviews] = useState({});
  const [animatedCount, setAnimatedCount] = useState(0);
  const sliderRef = useRef(null);
  const hasAnimated = useRef(false);
  const [forceRefresh, setForceRefresh] = useState(0);

  useEffect(() => {
    fetchReviews();
  }, []);

  // Animation du compteur - Force refresh
  useEffect(() => {
    if (reviewsData) {
      // Reset animation state
      hasAnimated.current = false;
      setAnimatedCount(0);
      
      // Start animation after a small delay
      const startTimer = setTimeout(() => {
        if (!hasAnimated.current) {
          hasAnimated.current = true;
          const targetCount = reviewsData.total_reviews;
          const duration = 3000; // 3 secondes pour voir l'animation
          const steps = Math.min(targetCount, 50); // Adapter le nombre d'étapes
          const increment = targetCount / steps;
          let currentStep = 0;

          const timer = setInterval(() => {
            currentStep++;
            if (currentStep >= steps) {
              setAnimatedCount(targetCount);
              clearInterval(timer);
            } else {
              const newCount = Math.floor(increment * currentStep);
              setAnimatedCount(newCount);
            }
          }, duration / steps);

          return () => clearInterval(timer);
        }
      }, 500); // Délai plus long pour voir le démarrage

      return () => clearTimeout(startTimer);
    }
  }, [reviewsData, forceRefresh]);

  const fetchReviews = async () => {
    try {
      const url = `${API_BASE_URL}/google-reviews`;
      const response = await axios.get(url);
      if (response.data.success) {
        setReviewsData(response.data.data);
        // Force refresh animation
        setForceRefresh(prev => prev + 1);
      } else {
        setError('API returned success: false');
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des avis:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={18}
            className={star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}
          />
        ))}
      </div>
    );
  };

  const formatTimeAgo = (relativeTime) => {
    const translations = {
      'in the last week': 'il y a quelques jours',
      'a week ago': 'il y a 1 semaine',
      '2 weeks ago': 'il y a 2 semaines',
      '3 weeks ago': 'il y a 3 semaines',
      'a month ago': 'il y a 1 mois',
      '2 months ago': 'il y a 2 mois',
      '3 months ago': 'il y a 3 mois',
      '4 months ago': 'il y a 4 mois',
      '5 months ago': 'il y a 5 mois',
      '6 months ago': 'il y a 6 mois',
      'a year ago': 'il y a 1 an',
      '2 years ago': 'il y a 2 ans'
    };
    return translations[relativeTime] || relativeTime;
  };

  const toggleExpanded = (index) => {
    setExpandedReviews(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  if (loading) {
    return (
      <section className="py-20 bg-gradient-to-b from-gray-900/50 to-gray-900/30">
        <div className="container">
          <div className="text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-700 rounded w-64 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-700 rounded w-96 mx-auto"></div>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error || !reviewsData) {
    return null;
  }

  // RÈGLE : Filtrer pour ne garder QUE les avis avec 4 ou 5 étoiles
  const highRatedReviews = reviewsData.reviews.filter(review => review.rating >= 4);
  
  // Afficher TOUS les avis 4★ et 5★ (sans limite)
  const topReviews = highRatedReviews;

  // Configuration du carousel
  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 6000,
    pauseOnHover: true,
    arrows: false,
    adaptiveHeight: false, // Garde la même hauteur
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
          dots: true
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          dots: true,
          centerMode: false
        }
      }
    ]
  };

  return (
    <section className="py-16 md:py-20 relative overflow-hidden" data-testid="google-reviews-section">
      <div className="container relative z-10 px-4">
        {/* En-tête sobre */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-10 md:mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold heading-display text-white mb-10 md:mb-12">
            Témoignages
          </h2>
          
          {/* Statistiques minimalistes - 2 cartes */}
          <div className="grid grid-cols-2 gap-6 md:gap-8 mb-8 md:mb-10 max-w-2xl mx-auto">
            {/* Note globale */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1, duration: 0.4 }}
              className="bg-gradient-to-br from-white/3 to-white/[0.01] border border-white/8 backdrop-blur-sm p-6 md:p-9 rounded-xl text-center hover:border-white/12 hover:bg-white/[0.04] transition-all duration-300"
            >
              {/* Étoiles avec animation */}
              <div className="flex justify-center gap-1 mb-4 md:mb-5">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={20}
                    className="fill-yellow-400 text-yellow-400 transition-transform hover:scale-110 duration-200"
                  />
                ))}
              </div>
              {/* Note */}
              <div className="text-4xl md:text-5xl font-bold text-gray-200 mb-2">
                {reviewsData.rating}/5
              </div>
              {/* Label */}
              <p className="text-gray-400 text-xs uppercase tracking-wider">Note</p>
            </motion.div>

            {/* Nombre d'avis */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.4 }}
              className="bg-gradient-to-br from-white/3 to-white/[0.01] border border-white/8 backdrop-blur-sm p-6 md:p-9 rounded-xl text-center hover:border-white/12 hover:bg-white/[0.04] transition-all duration-300"
            >
              {/* Nombre avec animation */}
              <div className="text-4xl md:text-5xl font-bold text-blue-400 mb-2 mt-9 md:mt-10">
                {animatedCount}
              </div>
              {/* Label */}
              <p className="text-gray-400 text-xs uppercase tracking-wider">Avis</p>
            </motion.div>
          </div>
        </motion.div>

        {/* Carousel d'avis */}
        <div className="relative mb-12 md:mb-12 -mx-4 md:mx-0">
          {/* Boutons de navigation custom */}
          <button
            onClick={() => sliderRef.current?.slickPrev()}
            className="absolute left-0 top-1/2 -translate-y-1/2 z-10 glass w-12 h-12 rounded-full flex items-center justify-center hover:bg-blue-500/20 transition-all -ml-6 hidden lg:flex"
            aria-label="Avis précédent"
          >
            <ChevronLeft className="text-white" size={24} />
          </button>
          
          <button
            onClick={() => sliderRef.current?.slickNext()}
            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 glass w-12 h-12 rounded-full flex items-center justify-center hover:bg-blue-500/20 transition-all -mr-6 hidden lg:flex"
            aria-label="Avis suivant"
          >
            <ChevronRight className="text-white" size={24} />
          </button>

          <Slider ref={sliderRef} {...sliderSettings}>
            {topReviews.map((review, index) => {
              const isExpanded = expandedReviews[index];
              const shouldTruncate = review.text && review.text.length > 120;
              const displayText = shouldTruncate && !isExpanded 
                ? `${review.text.substring(0, 120)}...` 
                : review.text;

              return (
                <div key={index} className="px-2 md:px-3 pb-2">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1, duration: 0.5 }}
                    whileHover={{ y: -5 }}
                    className="bg-gradient-to-br from-white/5 to-white/[0.02] border border-white/10 backdrop-blur-sm p-5 md:p-7 hover:bg-white/[0.07] hover:border-white/15 transition-all duration-300 rounded-xl flex flex-col h-[260px] md:h-[300px]"
                    data-testid={`review-card-${index}`}
                  >
                    {/* Badge vérifié et étoiles */}
                    <div className="flex items-center justify-between mb-3 md:mb-4">
                      <div className="flex items-center gap-1 text-green-400/80 text-[9px] md:text-[10px]">
                        <CheckCircle size={10} className="md:w-3 md:h-3" />
                        <span className="hidden sm:inline uppercase tracking-wide">Vérifié</span>
                      </div>
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            size={14}
                            className={star <= review.rating ? 'fill-yellow-400 text-yellow-400 transition-transform hover:scale-110 duration-200' : 'text-gray-400'}
                          />
                        ))}
                      </div>
                    </div>

                    {/* Auteur */}
                    <div className="flex items-center gap-3 md:gap-4 mb-3 md:mb-4">
                      {review.author_photo ? (
                        <img
                          src={review.author_photo}
                          alt={review.author_name}
                          className="w-9 h-9 md:w-11 md:h-11 rounded-full ring-2 ring-blue-500/30 flex-shrink-0"
                        />
                      ) : (
                        <div className="w-9 h-9 md:w-11 md:h-11 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-xs md:text-sm ring-2 ring-blue-500/30 flex-shrink-0">
                          {review.author_name.charAt(0)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-white text-xs md:text-sm truncate mb-1">{review.author_name}</p>
                        <div className="flex items-center gap-1.5 text-gray-500 text-[10px] md:text-xs">
                          <Calendar size={11} className="flex-shrink-0" />
                          <span>{formatTimeAgo(review.relative_time)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Texte de l'avis - avec hauteur fixe */}
                    <div className="flex-1 overflow-hidden">
                      {review.text ? (
                        <div className="h-full flex flex-col">
                          <p className={`text-gray-300 text-xs md:text-sm leading-relaxed ${!isExpanded ? 'line-clamp-4 md:line-clamp-4' : ''}`}>
                            {displayText}
                          </p>
                          {shouldTruncate && (
                            <button
                              onClick={() => toggleExpanded(index)}
                              className="text-blue-400 text-[10px] md:text-xs hover:text-blue-300 transition-colors mt-1 md:mt-2 inline-block"
                            >
                              {isExpanded ? '− Moins' : '+ Plus'}
                            </button>
                          )}
                        </div>
                      ) : (
                        <p className="text-gray-400 text-xs md:text-sm italic">
                          ⭐ Client satisfait
                        </p>
                      )}
                    </div>
                  </motion.div>
                </div>
              );
            })}
          </Slider>
        </div>

        {/* Badge Google discret et professionnel */}
        <motion.div 
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6, duration: 0.5 }}
          className="text-center"
        >
          <div className="inline-flex items-center gap-2 md:gap-3 bg-white/3 border border-white/8 px-4 md:px-6 py-2 md:py-3 rounded-lg">
            <CheckCircle className="text-green-400/80 flex-shrink-0" size={14} />
            <span className="text-gray-400 font-medium text-xs md:text-sm">Avis certifiés Google</span>
          </div>
        </motion.div>
      </div>

      {/* Style custom pour les dots du carousel et line-clamp */}
      <style jsx>{`
        :global(.slick-dots) {
          bottom: -35px !important;
        }
        :global(.slick-dots li button:before) {
          color: #60a5fa !important;
          font-size: 10px !important;
        }
        :global(.slick-dots li.slick-active button:before) {
          color: #3b82f6 !important;
        }
        :global(.line-clamp-4) {
          display: -webkit-box;
          -webkit-line-clamp: 4;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </section>
  );
};

export default GoogleReviewsPremium;
